import React, { useEffect, useState } from "react";
import {
    getRolesApi,
    addRoleApi,
    updateRoleApi,
    deleteRoleApi,
    getRoleBrandsApi,
    addRoleBrandApi,
    deleteRoleBrandApi,
} from "../../../apis/apis.hrm/apisRoles";
import { fetchedBrands } from "../../../apis/apis.logistics/apisProduct";
import { useHooksAuth } from "../../../hooks";
import { Modal } from "../../../components/ui";
import swal from "sweetalert2";

const inputClass =
    "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const thBase =
    "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300";
const tdBase =
    "px-3 py-2 text-xs whitespace-nowrap border-b border-gray-100 dark:border-gray-700 align-middle";

export function PageRole() {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newRole, setNewRole] = useState("");
    const [editingRole, setEditingRole] = useState(null);
    const [editValue, setEditValue] = useState("");
    const { auth } = useHooksAuth();

    // Marcas permitidas por rol
    const [brands, setBrands] = useState([]);
    const [showBrandsModal, setShowBrandsModal] = useState(false);
    const [activeRole, setActiveRole] = useState(null);
    const [activeRoleBrands, setActiveRoleBrands] = useState([]);
    const [loadingRoleBrands, setLoadingRoleBrands] = useState(false);
    const [selectedBrandID, setSelectedBrandID] = useState("");
    const [updatingAllBrands, setUpdatingAllBrands] = useState(false);

    const loadRoles = async () => {
        try {
            setLoading(true);
            const response = await getRolesApi();
            setRoles(response);
            setLoading(false);
        } catch (error) {
            console.error(error);
        }
    };

    useEffect(() => {
        loadRoles();
    }, []);

    const addRole = async () => {
        if (!newRole.trim()) return;

        try {
            await addRoleApi({ name: newRole });
            setNewRole("");
            loadRoles();
        } catch (error) {
            console.error(error);
        }
    };

    const updateRole = async (id) => {
        if (!editValue.trim()) return;

        try {
            await updateRoleApi({ roleId: id, name: editValue }, auth.token);
            setEditingRole(null);
            setEditValue("");
            loadRoles();
        } catch (error) {
            console.error(error);
        }
    };

    const deleteRole = async (id) => {
        const result = await swal.fire({
            title: "¿Estás seguro?",
            text: "¿Eliminar este rol?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#dc2626",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar",
        });
        if (result.isConfirmed) {
            try {
                await deleteRoleApi(id);
                loadRoles();
                swal.fire({
                    title: "Eliminado!",
                    text: "El rol ha sido eliminado.",
                    icon: "success",
                    timer: 1500,
                    showConfirmButton: false,
                });
            } catch (error) {
                console.error(error);
            }
        }
    };

    const startEdit = (role) => {
        setEditingRole(role.id);
        setEditValue(role.name);
    };

    const cancelEdit = () => {
        setEditingRole(null);
        setEditValue("");
    };

    const loadActiveRoleBrands = async (roleId) => {
        try {
            setLoadingRoleBrands(true);
            const response = await getRoleBrandsApi(roleId);
            setActiveRoleBrands(response);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingRoleBrands(false);
        }
    };

    const openBrandsModal = async (role) => {
        setActiveRole(role);
        setSelectedBrandID("");
        setShowBrandsModal(true);
        if (brands.length === 0) {
            try {
                const response = await fetchedBrands();
                setBrands(response);
            } catch (error) {
                console.error(error);
            }
        }
        loadActiveRoleBrands(role.id);
    };

    const closeBrandsModal = () => {
        setShowBrandsModal(false);
        setActiveRole(null);
        setActiveRoleBrands([]);
        setSelectedBrandID("");
    };

    const addBrandToRole = async () => {
        if (!selectedBrandID || !activeRole) return;

        try {
            const result = await addRoleBrandApi({
                roleID: activeRole.id,
                productBrandID: Number(selectedBrandID),
            });
            if (result.status === 201) {
                setSelectedBrandID("");
                loadActiveRoleBrands(activeRole.id);
            } else {
                swal.fire({
                    title: "Aviso",
                    text:
                        result.message ||
                        "No se pudo asignar la marca a este rol",
                    icon: "warning",
                });
            }
        } catch (error) {
            console.error(error);
        }
    };

    const removeBrandFromRole = async (roleBrandId) => {
        const result = await swal.fire({
            title: "¿Estás seguro?",
            text: "¿Quitar esta marca del rol?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#16a34a",
            cancelButtonColor: "#dc2626",
            confirmButtonText: "Sí, quitar",
            cancelButtonText: "Cancelar",
        });
        if (result.isConfirmed && activeRole) {
            try {
                await deleteRoleBrandApi(roleBrandId);
                loadActiveRoleBrands(activeRole.id);
            } catch (error) {
                console.error(error);
            }
        }
    };

    const toggleAllBrands = async (checked) => {
        if (!activeRole || updatingAllBrands) return;

        if (!checked) {
            const result = await swal.fire({
                title: "¿Estás seguro?",
                text: "Se quitarán todas las marcas asignadas a este rol.",
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#16a34a",
                cancelButtonColor: "#dc2626",
                confirmButtonText: "Sí, quitar todas",
                cancelButtonText: "Cancelar",
            });
            if (!result.isConfirmed) return;
        }

        try {
            setUpdatingAllBrands(true);
            if (checked) {
                await Promise.all(
                    availableBrandsToAdd.map((brand) =>
                        addRoleBrandApi({
                            roleID: activeRole.id,
                            productBrandID: Number(brand.productBrandID),
                        }),
                    ),
                );
            } else {
                await Promise.all(
                    activeRoleBrands.map((roleBrand) =>
                        deleteRoleBrandApi(roleBrand.roleBrandID),
                    ),
                );
            }
            await loadActiveRoleBrands(activeRole.id);
        } catch (error) {
            console.error(error);
        } finally {
            setUpdatingAllBrands(false);
        }
    };

    const availableBrandsToAdd = brands.filter(
        (b) =>
            !activeRoleBrands.some(
                (rb) => Number(rb.productBrandID) === Number(b.productBrandID),
            ),
    );
    const allBrandsSelected =
        brands.length > 0 && availableBrandsToAdd.length === 0;

    return (
        <>
            {/* Page Header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Gestión de Roles
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">
                                    RR.HH.
                                </li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">
                                    /
                                </li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">
                                    Roles
                                </li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            {/* Main Content */}
            <section>
                <div className="max-w-full px-4 space-y-4">
                    {/* Add Role Form */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <i className="fas fa-plus-circle text-blue-500"></i>
                                Agregar Nuevo Rol
                            </h2>
                        </div>
                        <div className="p-5">
                            <div className="flex flex-col sm:flex-row gap-3">
                                <div className="flex-grow-1">
                                    <input
                                        type="text"
                                        className={inputClass}
                                        placeholder="Ingresa el nombre del rol"
                                        value={newRole}
                                        onChange={(e) =>
                                            setNewRole(e.target.value)
                                        }
                                        onKeyPress={(e) =>
                                            e.key === "Enter" && addRole()
                                        }
                                    />
                                </div>
                                <button
                                    onClick={addRole}
                                    disabled={!newRole.trim()}
                                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-6 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50"
                                >
                                    <i className="fas fa-plus"></i>
                                    Agregar Rol
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Roles List */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <i className="fas fa-users text-blue-500"></i>
                                Lista de Roles
                            </h2>
                        </div>
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <div className="h-10 w-10 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin"></div>
                                <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                                    Cargando roles...
                                </p>
                            </div>
                        ) : roles.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-16 gap-3">
                                <i className="fas fa-users text-gray-300 dark:text-gray-600 text-5xl"></i>
                                <p className="text-base font-semibold text-gray-600 dark:text-gray-400">
                                    No hay roles registrados
                                </p>
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                    Comienza agregando tu primer rol
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* Desktop Table */}
                                <div className="hidden md:block overflow-x-auto bg-white dark:bg-gray-800">
                                    <table className="min-w-full text-sm border-separate border-spacing-0">
                                        <thead className="sticky top-0 z-10">
                                            <tr>
                                                <th
                                                    className={`${thBase} text-left`}
                                                >
                                                    Nombre del Rol
                                                </th>
                                                <th className={thBase}>
                                                    Acciones
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {roles.map((role, index) => (
                                                <tr
                                                    key={role.id || index}
                                                    className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                                                >
                                                    <td className={tdBase}>
                                                        {editingRole ===
                                                        role.id ? (
                                                            <input
                                                                type="text"
                                                                className={
                                                                    inputClass
                                                                }
                                                                value={
                                                                    editValue
                                                                }
                                                                onChange={(e) =>
                                                                    setEditValue(
                                                                        e.target
                                                                            .value,
                                                                    )
                                                                }
                                                                onKeyPress={(
                                                                    e,
                                                                ) =>
                                                                    e.key ===
                                                                        "Enter" &&
                                                                    updateRole(
                                                                        role.id,
                                                                    )
                                                                }
                                                                autoFocus
                                                            />
                                                        ) : (
                                                            <div className="flex items-center gap-2">
                                                                <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                                                    <i className="fas fa-user"></i>
                                                                </div>
                                                                <span className="font-semibold text-gray-700 dark:text-gray-300">
                                                                    {role.name}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className={tdBase}>
                                                        <div className="flex items-center justify-center gap-1">
                                                            {editingRole ===
                                                            role.id ? (
                                                                <>
                                                                    <button
                                                                        onClick={() =>
                                                                            updateRole(
                                                                                role.id,
                                                                            )
                                                                        }
                                                                        className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-xs font-medium px-3 py-1 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                                    >
                                                                        <i className="fas fa-check"></i>
                                                                        Guardar
                                                                    </button>
                                                                    <button
                                                                        onClick={
                                                                            cancelEdit
                                                                        }
                                                                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-xs font-medium px-3 py-1 transition-colors"
                                                                    >
                                                                        <i className="fas fa-times"></i>
                                                                        Cancelar
                                                                    </button>
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        onClick={() =>
                                                                            openBrandsModal(
                                                                                role,
                                                                            )
                                                                        }
                                                                        className="inline-flex items-center gap-1 rounded-md border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-xs font-medium px-2 py-1 transition-colors"
                                                                    >
                                                                        <i className="fas fa-tags"></i>
                                                                        Marcas
                                                                    </button>
                                                                    <button
                                                                        onClick={() =>
                                                                            startEdit(
                                                                                role,
                                                                            )
                                                                        }
                                                                        className="inline-flex items-center gap-1 rounded-md border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-xs font-medium px-2 py-1 transition-colors"
                                                                    >
                                                                        <i className="fas fa-edit"></i>
                                                                        Editar
                                                                    </button>
                                                                    <button
                                                                        onClick={() =>
                                                                            deleteRole(
                                                                                role.id,
                                                                            )
                                                                        }
                                                                        className="inline-flex items-center gap-1 rounded-md border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs font-medium px-2 py-1 transition-colors"
                                                                    >
                                                                        <i className="fas fa-trash"></i>
                                                                        Eliminar
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Cards */}
                                <div className="md:hidden divide-y divide-gray-100 dark:divide-gray-700">
                                    {roles.map((role, index) => (
                                        <div
                                            key={role.id || index}
                                            className="p-4"
                                        >
                                            {editingRole === role.id ? (
                                                <div className="space-y-3">
                                                    <input
                                                        type="text"
                                                        className={inputClass}
                                                        value={editValue}
                                                        onChange={(e) =>
                                                            setEditValue(
                                                                e.target.value,
                                                            )
                                                        }
                                                        onKeyPress={(e) =>
                                                            e.key === "Enter" &&
                                                            updateRole(role.id)
                                                        }
                                                        autoFocus
                                                    />
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() =>
                                                                updateRole(
                                                                    role.id,
                                                                )
                                                            }
                                                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                                                        >
                                                            <i className="fas fa-check"></i>
                                                            Guardar
                                                        </button>
                                                        <button
                                                            onClick={cancelEdit}
                                                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium px-4 py-2 transition-colors"
                                                        >
                                                            <i className="fas fa-times"></i>
                                                            Cancelar
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="flex items-center justify-between mb-3">
                                                        <div className="flex items-center gap-2">
                                                            <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                                                                <i className="fas fa-user"></i>
                                                            </div>
                                                            <p className="text-sm font-semibold text-gray-900 dark:text-white">
                                                                {role.name}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() =>
                                                                openBrandsModal(
                                                                    role,
                                                                )
                                                            }
                                                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm font-medium px-4 py-2 transition-colors"
                                                        >
                                                            <i className="fas fa-tags"></i>
                                                            Marcas
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                startEdit(role)
                                                            }
                                                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20 text-sm font-medium px-4 py-2 transition-colors"
                                                        >
                                                            <i className="fas fa-edit"></i>
                                                            Editar
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                deleteRole(
                                                                    role.id,
                                                                )
                                                            }
                                                            className="flex-1 inline-flex items-center justify-center gap-1 rounded-lg border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium px-4 py-2 transition-colors"
                                                        >
                                                            <i className="fas fa-trash"></i>
                                                            Eliminar
                                                        </button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </section>

            {/* Marcas permitidas por rol */}
            <Modal
                isOpen={showBrandsModal}
                onClose={closeBrandsModal}
                title={`Marcas permitidas: ${activeRole ? activeRole.name : ""}`}
                size="lg"
            >
                <div className="space-y-4 -mt-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        Si un rol no tiene ninguna marca asignada, no se aplica
                        restricción y puede ver/vender todas las marcas.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-2">
                        <select
                            className={inputClass}
                            value={selectedBrandID}
                            onChange={(e) => setSelectedBrandID(e.target.value)}
                        >
                            <option value="">Selecciona una marca...</option>
                            {availableBrandsToAdd.map((b) => (
                                <option
                                    key={b.productBrandID}
                                    value={b.productBrandID}
                                >
                                    {b.name}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={addBrandToRole}
                            disabled={!selectedBrandID}
                            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 hover:bg-blue-700 active:bg-blue-800 text-white text-sm font-medium px-4 py-2 transition-colors disabled:opacity-50 whitespace-nowrap"
                        >
                            <i className="fas fa-plus"></i>
                            Asignar
                        </button>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                            type="checkbox"
                            checked={allBrandsSelected}
                            onChange={(e) => toggleAllBrands(e.target.checked)}
                            disabled={updatingAllBrands || brands.length === 0}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span>
                            {updatingAllBrands
                                ? "Actualizando marcas..."
                                : "Marcar todas las marcas"}
                        </span>
                    </label>

                    {loadingRoleBrands ? (
                        <div className="py-8 text-center">
                            <div className="mx-auto h-8 w-8 rounded-full border-4 border-blue-200 border-t-blue-600 animate-spin"></div>
                        </div>
                    ) : activeRoleBrands.length === 0 ? (
                        <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            Este rol no tiene marcas asignadas (ve todas las
                            marcas).
                        </div>
                    ) : (
                        <ul className="divide-y divide-gray-100 dark:divide-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                            {activeRoleBrands.map((rb) => (
                                <li
                                    key={rb.roleBrandID}
                                    className="flex items-center justify-between px-3 py-2 text-sm bg-white dark:bg-gray-800"
                                >
                                    <span className="text-gray-700 dark:text-gray-300">
                                        {rb.productBrandName}
                                    </span>
                                    <button
                                        onClick={() =>
                                            removeBrandFromRole(rb.roleBrandID)
                                        }
                                        className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-xs font-medium"
                                    >
                                        <i className="fas fa-times"></i>
                                        Quitar
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}

                    <div className="flex justify-end border-t border-gray-200 pt-4 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={closeBrandsModal}
                            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </Modal>
        </>
    );
}
